import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CircleAlert } from 'lucide-react';
import { getCourseStudents, getStudentProgress } from '../../api/enrollments';
import { unwrapList, unwrapMeta } from '../../utils/api';
import { formatDate, formatNumber, formatPercent, formatRelative } from '../../utils/datetime';
import { usePagination } from '../../hooks/usePagination';
import { useScrollFade } from '../../hooks/useScrollFade';
import { useCourseTitle } from '../../hooks/useCourseTitle';
import Avatar from '../../components/Avatar/Avatar';
import Modal from '../../components/Modal/Modal';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import PageHead from '../../components/PageHead/PageHead';
import ProgressBar from '../../components/ProgressBar/ProgressBar';
import LoadingState from '../../components/LoadingState/LoadingState';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import Dropdown from '../../components/Dropdown/Dropdown';
import Pagination from '../../components/Pagination/Pagination';
import SearchInput from '../../components/SearchInput/SearchInput';
import TableCard from '../../components/TableCard/TableCard';
import { useI18n } from '../../i18n/I18nContext';
import './StudentProgress.css';

const SEARCH_DEBOUNCE_MS = 400;
const PAGE_SIZE = 20;
// More rows than this and the table caps its height, so its head stays in view while it scrolls.
const TALL_ROWS = 12;
// Backend StudentListStatus enum — class-validator's @IsEnum rejects anything else, including
// lowercase values.
const STATUS_FILTERS = ['', 'ACTIVE', 'COMPLETED', 'AT_RISK', 'INACTIVE'];

const clampPercent = (value) => Math.max(0, Math.min(100, Math.round(Number(value) || 0)));

function ProgressMeter({ value, label }) {
  const percent = clampPercent(value);
  return (
    <div className={`spr-progress${percent >= 100 ? ' is-complete' : ''}`}>
      <ProgressBar className="spr-bar" value={percent} label={label} />
      <span className="spr-pct">{formatPercent(percent, { decimals: 0 })}</span>
    </div>
  );
}

function StudentProgress({ courseId: courseIdProp } = {}) {
  const { t } = useI18n();
  const params = useParams();
  const courseId = courseIdProp || params.courseId || params.id;
  const courseTitle = useCourseTitle(courseId);
  const [students, setStudents] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [studentDetail, setStudentDetail] = useState(null);
  const [detailState, setDetailState] = useState('idle'); // idle | loading | error
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [denied, setDenied] = useState(false);
  const [search, setSearch] = useState('');
  // What the list is loaded with: `search` a beat after the last keystroke.
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('progress');
  const pagination = usePagination({ limit: PAGE_SIZE });
  const { page: paginationPage, limit: paginationLimit, applyMeta, reset: paginationReset } = pagination;
  // On a phone the status chips scroll sideways: the fade at the edge says there are more.
  const chips = useScrollFade();
  // Only the newest list request and the newest detail request may touch the page.
  const listRequest = useRef(0);
  const detailRequest = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    paginationReset();
  }, [courseId, query, statusFilter, paginationReset]);

  const fetchStudents = useCallback(async () => {
    const id = ++listRequest.current;
    try {
      setLoading(true);
      // Backend's ListStudentsQueryDto only accepts limit/offset/status/search
      // (forbidNonWhitelisted: true) — no `page` and no `sortBy` under any name.
      // Sorting is applied client-side below instead of being sent to the API.
      const result = await getCourseStudents(courseId, {
        limit: paginationLimit,
        offset: (paginationPage - 1) * paginationLimit,
        search: query || undefined,
        status: statusFilter || undefined,
      });
      if (id !== listRequest.current) return;
      const list = unwrapList(result);
      setStudents(list);
      const meta = unwrapMeta(result, { total: list.length, limit: paginationLimit });
      applyMeta({
        total: meta.total,
        totalPages: result?.meta?.totalPages || Math.max(1, Math.ceil(meta.total / paginationLimit)),
      });
      setError(null);
      setDenied(false);
    } catch (err) {
      if (id !== listRequest.current) return;
      setDenied(err.code === 403);
      setError(err.code === 403
        ? t('courseInsights.progress.accessDenied')
        : t('courseInsights.progress.errorLoad'));
    } finally {
      if (id === listRequest.current) setLoading(false);
    }
  }, [courseId, query, statusFilter, paginationPage, paginationLimit, applyMeta, t]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  // Another course's student must never stay open under this course.
  useEffect(() => {
    setSelectedId(null);
    setStudentDetail(null);
    setDetailState('idle');
  }, [courseId]);

  // The backend has no sort parameter, so the order is applied here, to the page that is loaded.
  // That is only the whole list while it fits one page: with more pages, "most advanced first"
  // would restart on every page, so the control is offered (and applied) for a single page only.
  // Backend StudentListItemDto fields: userId, name, email, enrollmentStatus,
  // progress, lastActivityAt (no timeSpent — that option has been removed).
  const canSort = pagination.totalPages <= 1;
  const sortedStudents = useMemo(() => {
    if (!canSort) return students;
    const list = [...students];
    if (sortBy === 'lastAccessed') {
      list.sort((a, b) => new Date(b.lastActivityAt || 0) - new Date(a.lastActivityAt || 0));
    } else {
      // 'progress' (default) and any unrecognised value
      list.sort((a, b) => (b.progress || 0) - (a.progress || 0));
    }
    return list;
  }, [students, sortBy, canSort]);

  // Backend's StudentListStatus enum is ACTIVE/COMPLETED/INACTIVE/AT_RISK —
  // map it to a StatusBadge tone (StatusBadge's own tone map doesn't cover
  // AT_RISK) and a translated label for the pill.
  const getStatusMeta = (value) => {
    const normalized = String(value || '').toUpperCase();
    switch (normalized) {
      case 'ACTIVE':
        return { tone: 'success', label: t('courseInsights.progress.badgeActive') };
      case 'COMPLETED':
        return { tone: 'success', label: t('courseInsights.progress.badgeCompleted') };
      case 'AT_RISK':
        return { tone: 'warning', label: t('courseInsights.progress.badgeAtRisk') };
      case 'INACTIVE':
        return { tone: 'danger', label: t('courseInsights.progress.badgeInactive') };
      default:
        return { tone: 'neutral', label: t('courseInsights.progress.statusUnknown') };
    }
  };

  const filterLabel = (value) => {
    switch (value) {
      case 'ACTIVE': return t('courseInsights.progress.statusActive');
      case 'COMPLETED': return t('courseInsights.progress.statusCompleted');
      case 'AT_RISK': return t('courseInsights.progress.statusAtRisk');
      case 'INACTIVE': return t('courseInsights.progress.statusInactive');
      default: return t('courseInsights.progress.statusAll');
    }
  };

  // A row opens the student's quick look: a drawer from the right, so the table keeps its width.
  const handleViewStudent = async (studentId) => {
    if (!studentId) return;
    const id = ++detailRequest.current;
    setSelectedId(studentId);
    setStudentDetail(null);
    setDetailState('loading');
    try {
      const detail = await getStudentProgress(courseId, studentId);
      if (id !== detailRequest.current) return;
      setStudentDetail(detail);
      setDetailState('idle');
    } catch {
      if (id !== detailRequest.current) return;
      setStudentDetail(null);
      setDetailState('error');
    }
  };

  const closeDetail = () => {
    detailRequest.current += 1;
    setSelectedId(null);
    setStudentDetail(null);
    setDetailState('idle');
  };

  const clearFilters = () => {
    setSearch('');
    setQuery('');
    setStatusFilter('');
  };

  const hasFilters = Boolean(query || statusFilter);

  let results;
  if (loading && students.length === 0 && !error) {
    results = <LoadingState skeleton="table" rows={6} message={t('courseInsights.progress.loading')} />;
  } else if (students.length === 0) {
    if (error) {
      results = (
        <PageEmptyState
          variant={denied ? 'forbidden' : 'error'}
          description={error}
          onRetry={denied ? undefined : fetchStudents}
        />
      );
    } else if (hasFilters) {
      results = (
        <PageEmptyState
          variant="search"
          title={t('courseInsights.progress.filteredEmptyTitle')}
          description={t('courseInsights.progress.filteredEmptyDesc')}
          actionLabel={t('courseInsights.common.clearFilters')}
          onAction={clearFilters}
        />
      );
    } else {
      results = (
        <PageEmptyState
          variant="students"
          title={t('courseInsights.progress.emptyTitle')}
          description={t('courseInsights.progress.emptyDesc')}
        />
      );
    }
  } else {
    // Column keys must match StudentListItemDto fields exactly:
    // userId, name, email, enrollmentStatus, progress, lastActivityAt.
    results = (
      <TableCard
        className="spr-table-card"
        tall={sortedStudents.length > TALL_ROWS}
        aria-label={t('courseInsights.progress.title')}
        footer={(
          <div className="spr-foot">
            <span className="spr-count">{t('courseInsights.progress.countStudents', { count: formatNumber(pagination.total, { decimals: 0 }) })}</span>
            <Pagination state={pagination} hideOnSinglePage />
          </div>
        )}
      >
        <table className="ui-table ui-table--stack spr-table">
          <thead>
            <tr>
              <th>{t('courseInsights.progress.colName')}</th>
              <th>{t('courseInsights.progress.colStatus')}</th>
              <th className="spr-th-progress">{t('courseInsights.progress.colProgress')}</th>
              <th>{t('courseInsights.progress.colLastAccessed')}</th>
            </tr>
          </thead>
          <tbody>
            {sortedStudents.map((row) => {
              const name = row.name || row.email;
              const meta = row.enrollmentStatus ? getStatusMeta(row.enrollmentStatus) : null;
              const open = selectedId === row.userId;
              return (
                <tr
                  key={row.userId}
                  className="is-clickable"
                  tabIndex={0}
                  aria-selected={open}
                  onClick={() => handleViewStudent(row.userId)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleViewStudent(row.userId);
                    }
                  }}
                >
                  <td>
                    <div className="ui-cell-person">
                      <Avatar name={name} id={row.userId || name} />
                      <span className="ui-cell-person__text">
                        <span className="ui-cell-person__name">{name}</span>
                        {row.name && row.email && <span className="ui-cell-person__sub">{row.email}</span>}
                      </span>
                    </div>
                  </td>
                  <td data-label={t('courseInsights.progress.colStatus')}>
                    {meta
                      ? <StatusBadge status={row.enrollmentStatus} tone={meta.tone}>{meta.label}</StatusBadge>
                      : t('courseInsights.progress.statusUnknown')}
                  </td>
                  <td data-label={t('courseInsights.progress.colProgress')}>
                    <ProgressMeter value={row.progress} label={t('courseInsights.progress.colProgress')} />
                  </td>
                  <td className="spr-muted" data-label={t('courseInsights.progress.colLastAccessed')}>
                    {row.lastActivityAt ? formatRelative(row.lastActivityAt) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableCard>
    );
  }

  // The drawer: the row it was opened from names it at once; the details fill in when they land.
  // StudentProgressDetailDto fields (from teacher.service.ts):
  // studentName, studentEmail, overallProgress, enrolledAt, completedAt?,
  // lessonProgress[], quizAttempts[], avgQuizScore, lessonsCompleted, totalLessons
  const selectedRow = students.find((row) => row.userId === selectedId) || null;
  const detailName = (studentDetail && (studentDetail.studentName || studentDetail.studentEmail))
    || (selectedRow && (selectedRow.name || selectedRow.email))
    || '';
  const detailEmail = (studentDetail && studentDetail.studentEmail) || (selectedRow && selectedRow.email) || '';
  const detailStatus = studentDetail
    ? getStatusMeta(selectedRow?.enrollmentStatus || (studentDetail.completedAt ? 'COMPLETED' : 'ACTIVE'))
    : null;
  const detailOpen = Boolean(selectedId);

  return (
    <div className="ui-stack spr-page">
      <PageHead
        back={{ to: `/courses/edit/${courseId}?tab=analytics`, label: t('courseInsights.common.backToAnalytics') }}
        eyebrow={<span className="spr-eyebrow">{courseTitle || t('courseInsights.common.parent')}</span>}
        title={t('courseInsights.progress.title')}
        description={t('courseInsights.analytics.navProgressDesc')}
      />

      <div className="spr-toolbar">
        <SearchInput
          className="spr-search"
          value={search}
          onChange={setSearch}
          placeholder={t('courseInsights.progress.searchPlaceholder')}
        />
        <div
          ref={chips.ref}
          className={`ui-chip-row spr-filters ${chips.fade}`.trim()}
          role="group"
          aria-label={t('courseInsights.progress.statusFilterAria')}
        >
          {STATUS_FILTERS.map((value) => (
            <button
              key={value || 'all'}
              type="button"
              className="ui-chip"
              aria-pressed={statusFilter === value}
              onClick={() => setStatusFilter(value)}
            >
              {filterLabel(value)}
            </button>
          ))}
        </div>
        {canSort && students.length > 1 && (
          <div className="spr-sort">
            <Dropdown
              size="sm"
              value={sortBy}
              onChange={(val) => setSortBy(val)}
              options={[
                { value: 'progress', label: t('courseInsights.progress.sortProgress') },
                { value: 'lastAccessed', label: t('courseInsights.progress.sortLastAccessed') },
              ]}
              ariaLabel={t('courseInsights.progress.sortAria')}
            />
          </div>
        )}
      </div>

      {error && students.length > 0 && (
        <div className="ui-alert" role="alert">
          <CircleAlert aria-hidden="true" />
          <p>{error}</p>
          <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={fetchStudents}>
            {t('ui.retry')}
          </button>
        </div>
      )}

      <div className="spr-results" aria-busy={loading}>
        {results}
      </div>

      <Modal
        open={detailOpen}
        onClose={closeDetail}
        variant="drawer"
        title={detailName}
        aria-label={t('courseInsights.progress.detailTitle')}
        footer={detailState === 'idle' && studentDetail ? (
          <Link
            className="ds-btn ds-btn-primary"
            to={`/students/${selectedId}?courseId=${courseId}`}
          >
            {t('courseInsights.progress.openProfile')}
          </Link>
        ) : undefined}
      >
        <div className="spr-detail">
          {detailState === 'loading' && (
            <LoadingState skeleton="form" rows={4} compact message={t('courseInsights.progress.loading')} />
          )}
          {detailState === 'error' && (
            <div className="ui-alert" role="alert">
              <CircleAlert aria-hidden="true" />
              <p>{t('courseInsights.progress.errorLoadDetails')}</p>
              <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={() => handleViewStudent(selectedId)}>
                {t('ui.retry')}
              </button>
            </div>
          )}
          {studentDetail && detailState === 'idle' && (
            <>
              <div className="spr-detail-who">
                <Avatar name={detailName} id={selectedId} size="lg" />
                <div className="spr-detail-text">
                  {detailEmail && <p className="spr-detail-email" title={detailEmail}>{detailEmail}</p>}
                  <StatusBadge tone={detailStatus.tone}>{detailStatus.label}</StatusBadge>
                </div>
              </div>

              {typeof studentDetail.overallProgress === 'number' && (
                <div className="spr-overall">
                  <span className="spr-overall-label">{t('courseInsights.progress.overallProgress')}</span>
                  <ProgressMeter value={studentDetail.overallProgress} label={t('courseInsights.progress.overallProgress')} />
                </div>
              )}

              <dl className="ui-kv">
                <div className="ui-kv__row">
                  <dt>{t('courseInsights.progress.detailEnrolled')}</dt>
                  <dd>{studentDetail.enrolledAt ? formatDate(studentDetail.enrolledAt) : '—'}</dd>
                </div>
              </dl>

              <div className="spr-metrics">
                <div className="section-card section-card--sunken spr-metric">
                  <span>{t('courseInsights.progress.lessonsStarted')}</span>
                  <strong>
                    {formatNumber(
                      studentDetail.lessonProgress
                        ? studentDetail.lessonProgress.filter((l) => l.started).length
                        : studentDetail.totalLessons || 0,
                      { decimals: 0 },
                    )}
                  </strong>
                </div>
                <div className="section-card section-card--sunken spr-metric">
                  <span>{t('courseInsights.progress.lessonsCompleted')}</span>
                  <strong>{formatNumber(studentDetail.lessonsCompleted || 0, { decimals: 0 })}</strong>
                </div>
                <div className="section-card section-card--sunken spr-metric">
                  <span>{t('courseInsights.progress.quizzesTaken')}</span>
                  <strong>{formatNumber(studentDetail.quizAttempts?.length || 0, { decimals: 0 })}</strong>
                </div>
                <div className="section-card section-card--sunken spr-metric">
                  <span>{t('courseInsights.progress.avgScore')}</span>
                  <strong>{formatPercent(studentDetail.avgQuizScore || 0, { decimals: 0 })}</strong>
                </div>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}

export default StudentProgress;
